import React, { useState, useMemo, useEffect } from 'react';
import { FoodItem, DailyRecord } from '../types';
import { 
  Apple, Utensils, Plus, Minus, Trash2, Calendar, FileText, ChevronRight, ChevronLeft, 
  CheckCircle2, ChevronDown, Award, RefreshCw, BarChart3, Clock, Sparkles, 
  AlertTriangle, X, Search, Star, MessageSquare, Send, Droplets, Scale, Zap, Info, ShieldCheck
} from 'lucide-react';
import { loadAppState, saveAppState, toPersianDigits } from '../lib/storage';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { formatGregorianDate } from '../lib/dateUtils';
import { sendWorkerAiRequest, parseNutritionReply, buildNutritionPrompt, estimateLocalNutrients, ParsedFoodNutrition } from '../lib/workerAi';
import { getLocalizedFoodItem } from '../lib/foodTranslations';
import AiChatBox from './AiChatBox';
import VoiceInputButton from './VoiceInputButton';
import { apiGet, apiPost, apiDelete } from '../lib/api';

interface NutritionProps {
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
  currentDate?: string;
  onSetDate?: (dateStr: string) => void;
}

interface FoodTemplate {
  name: string;
  quantity: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  category: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack';
  foodCategory: 'Breakfast' | 'Protein' | 'RiceGrains' | 'Vegetables' | 'Fruits' | 'Dairy' | 'Nuts' | 'Persian' | 'Drinks';
  baseGrams?: number;
}

// 9 Rich categories of quick foods
const getQuickFoodCategories = (t: any) => [
  { id: 'all', name: t('foodCat_all', 'همه خوراکی‌ها'), icon: '🍽️' },
  { id: 'Breakfast', name: t('foodCat_Breakfast', 'صبحانه'), icon: '🍳' },
  { id: 'Protein', name: t('foodCat_Protein', 'پروتئین‌ها'), icon: '🍗' },
  { id: 'RiceGrains', name: t('foodCat_RiceGrains', 'برنج و غلات'), icon: '🍚' },
  { id: 'Vegetables', name: t('foodCat_Vegetables', 'سبزیجات'), icon: '🥗' },
  { id: 'Fruits', name: t('foodCat_Fruits', 'میوه‌ها و مرکبات'), icon: '🍎' },
  { id: 'Dairy', name: t('foodCat_Dairy', 'لبنیات'), icon: '🥛' },
  { id: 'Nuts', name: t('foodCat_Nuts', 'مغزها و آجیل'), icon: '🥜' },
  { id: 'Persian', name: t('foodCat_Persian', 'غذاهای اصیل ایرانی'), icon: '🍲' },
  { id: 'Drinks', name: t('foodCat_Drinks', 'نوشیدنی‌ها'), icon: '☕' }
];

const EXTENDED_FOOD_TEMPLATES: FoodTemplate[] = [
  // 🍎 میوه‌ها و مرکبات (Fruits & Citrus)
  { name: 'سیب سرخ / زرد', quantity: '۱ عدد متوسط (۱۵۰ گرم)', calories: 80, protein: 0.5, carbs: 21, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 150 },
  { name: 'موز رسیده‌', quantity: '۱ عدد متوسط (۱۲۰ گرم)', calories: 105, protein: 1.3, carbs: 27, fat: 0.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 120 },
  { name: 'پرتقال تامسون', quantity: '۱ عدد متوسط (۱۸۰ گرم)', calories: 85, protein: 1.7, carbs: 21, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 180 },
  { name: 'نارنگی شیرین', quantity: '۱ عدد (۹۰ گرم)', calories: 47, protein: 0.7, carbs: 12, fat: 0.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 90 },
  { name: 'خیار بوته‌ای', quantity: '۲ عدد (۱۵۰ گرم)', calories: 22, protein: 1.0, carbs: 5, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 150 },
  { name: 'گوجه‌فرنگی تازه', quantity: '۱ عدد (۱۲۰ گرم)', calories: 22, protein: 1.1, carbs: 4.8, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 120 },
  { name: 'هندوانه شیرین', quantity: '۱ برش (۳۰۰ گرم)', calories: 90, protein: 1.8, carbs: 22, fat: 0.5, category: 'Snack', foodCategory: 'Fruits', baseGrams: 300 },
  { name: 'خربزه / طالبی / ملون', quantity: '۱ برش (۲۵۰ گرم)', calories: 85, protein: 2.1, carbs: 20, fat: 0.5, category: 'Snack', foodCategory: 'Fruits', baseGrams: 250 },
  { name: 'توت‌فرنگی تازه', quantity: '۱ کاسه (۱۵۰ گرم)', calories: 48, protein: 1.0, carbs: 11.7, fat: 0.5, category: 'Snack', foodCategory: 'Fruits', baseGrams: 150 },
  { name: 'انگور عسگری / یاقوتی', quantity: '۱ خوشه (۱۵۰ گرم)', calories: 104, protein: 1.1, carbs: 27, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 150 },
  { name: 'هلو / شلیل', quantity: '۱ عدد (۱۳۰ گرم)', calories: 50, protein: 1.2, carbs: 12, fat: 0.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 130 },
  { name: 'زردآلو تازه', quantity: '۳ عدد (۱۰۰ گرم)', calories: 48, protein: 1.4, carbs: 11, fat: 0.4, category: 'Snack', foodCategory: 'Fruits', baseGrams: 100 },
  { name: 'گیلاس / آلبالو', quantity: '۱ کاسه (۱۲۰ گرم)', calories: 60, protein: 1.2, carbs: 15, fat: 0.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 120 },
  { name: 'انار ساوه', quantity: '۱ عدد (۲۰۰ گرم)', calories: 166, protein: 3.3, carbs: 38, fat: 2.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 200 },
  { name: 'کیوی', quantity: '۲ عدد (۱۴۰ گرم)', calories: 85, protein: 1.6, carbs: 20, fat: 0.7, category: 'Snack', foodCategory: 'Fruits', baseGrams: 140 },
  { name: 'انجیر تازه', quantity: '۳ عدد (۱۰۰ گرم)', calories: 74, protein: 0.8, carbs: 19, fat: 0.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 100 },
  { name: 'انجیر خشک', quantity: '۴ عدد (۵۰ گرم)', calories: 125, protein: 1.6, carbs: 32, fat: 0.5, category: 'Snack', foodCategory: 'Fruits', baseGrams: 50 },
  { name: 'خرمالو شیرین', quantity: '۱ عدد (۱۰۰ گرم)', calories: 70, protein: 0.6, carbs: 18, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 100 },
  { name: 'لیمو شیرین', quantity: '۱ عدد (۱۵۰ گرم)', calories: 65, protein: 1.2, carbs: 16, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 150 },
  { name: 'خرما مضافتی / مدجول', quantity: '۳ عدد (۴۵ گرم)', calories: 125, protein: 1.0, carbs: 33, fat: 0.2, category: 'Snack', foodCategory: 'Fruits', baseGrams: 45 },
  { name: 'کشمش / مویز', quantity: 'یک مشت (۳۰ گرم)', calories: 90, protein: 1.0, carbs: 23, fat: 0.1, category: 'Snack', foodCategory: 'Fruits', baseGrams: 30 },
  { name: 'برگه زردآلو / قیسی', quantity: '۵۰ گرم', calories: 120, protein: 1.7, carbs: 31, fat: 0.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 50 },
  { name: 'انبه استوایی', quantity: '۱ عدد (۲۰۰ گرم)', calories: 120, protein: 1.6, carbs: 30, fat: 0.8, category: 'Snack', foodCategory: 'Fruits', baseGrams: 200 },
  { name: 'آناناس تازه', quantity: '۲ حلقه (۱۲۰ گرم)', calories: 60, protein: 0.6, carbs: 15, fat: 0.1, category: 'Snack', foodCategory: 'Fruits', baseGrams: 120 },
  { name: 'آووکادو', quantity: 'نصف عدد (۱۰۰ گرم)', calories: 160, protein: 2.0, carbs: 8.5, fat: 14.7, category: 'Snack', foodCategory: 'Fruits', baseGrams: 100 },
  { name: 'زیتون سبز / سیاه', quantity: '۱۰ عدد (۴۰ گرم)', calories: 58, protein: 0.4, carbs: 2.5, fat: 4.3, category: 'Snack', foodCategory: 'Fruits', baseGrams: 40 },

  // 🍲 غذاهای اصیل ایرانی (Persian Dishes)
  { name: 'خورشت قرمه‌سبزی با چلو', quantity: '۱ پرس کامل (۴۰۰ گرم)', calories: 560, protein: 28, carbs: 62, fat: 20, category: 'Lunch', foodCategory: 'Persian', baseGrams: 400 },
  { name: 'خورشت قیمه بادمجان با چلو', quantity: '۱ پرس کامل (۴۰۰ گرم)', calories: 580, protein: 26, carbs: 64, fat: 22, category: 'Lunch', foodCategory: 'Persian', baseGrams: 400 },
  { name: 'خورشت فسنجان مرغ با چلو', quantity: '۱ پرس کامل (۴۰۰ گرم)', calories: 640, protein: 24, carbs: 60, fat: 32, category: 'Lunch', foodCategory: 'Persian', baseGrams: 400 },
  { name: 'خورشت کرفس با چلو', quantity: '۱ پرس کامل (۴۰۰ گرم)', calories: 480, protein: 25, carbs: 58, fat: 16, category: 'Lunch', foodCategory: 'Persian', baseGrams: 400 },
  { name: 'خورشت بامیه با چلو', quantity: '۱ پرس کامل (۴۰۰ گرم)', calories: 490, protein: 24, carbs: 60, fat: 16, category: 'Lunch', foodCategory: 'Persian', baseGrams: 400 },
  { name: 'چلو کباب کوبیده (۲ سیخ)', quantity: '۱ پرس با برنج (۴۵۰ گرم)', calories: 720, protein: 38, carbs: 65, fat: 32, category: 'Lunch', foodCategory: 'Persian', baseGrams: 450 },
  { name: 'جوجه کباب زعفرانی با برنج', quantity: '۱ پرس با برنج (۴۰۰ گرم)', calories: 580, protein: 44, carbs: 62, fat: 14, category: 'Lunch', foodCategory: 'Persian', baseGrams: 400 },
  { name: 'کباب برگ گوسفندی', quantity: '۱ سیخ بدون برنج (۲۰۰ گرم)', calories: 380, protein: 42, carbs: 2, fat: 22, category: 'Lunch', foodCategory: 'Persian', baseGrams: 200 },
  { name: 'چلو ماهیچه گوسفندی', quantity: '۱ پرس با برنج (۴۵۰ گرم)', calories: 750, protein: 48, carbs: 62, fat: 34, category: 'Lunch', foodCategory: 'Persian', baseGrams: 450 },
  { name: 'زرشک پلو با مرغ', quantity: '۱ پرس کامل (۴۵۰ گرم)', calories: 620, protein: 42, carbs: 68, fat: 18, category: 'Lunch', foodCategory: 'Persian', baseGrams: 450 },
  { name: 'باقالی پلو با ماهیچه', quantity: '۱ پرس کامل (۵۰۰ گرم)', calories: 780, protein: 46, carbs: 75, fat: 32, category: 'Lunch', foodCategory: 'Persian', baseGrams: 500 },
  { name: 'ته چین مرغ زعفرانی', quantity: '۱ تکه بزرگ (۳۰۰ گرم)', calories: 520, protein: 32, carbs: 55, fat: 18, category: 'Lunch', foodCategory: 'Persian', baseGrams: 300 },
  { name: 'لوبیا پلو با گوشت تکه‌ای', quantity: '۱ پرس کامل (۳۵۰ گرم)', calories: 510, protein: 26, carbs: 62, fat: 16, category: 'Lunch', foodCategory: 'Persian', baseGrams: 350 },
  { name: 'عدس پلو با کشمش و خرما', quantity: '۱ پرس کامل (۳۵۰ گرم)', calories: 530, protein: 22, carbs: 78, fat: 12, category: 'Lunch', foodCategory: 'Persian', baseGrams: 350 },
  { name: 'استانبولی پلو با گوجه و گوشت', quantity: '۱ پرس کامل (۳۵۰ گرم)', calories: 480, protein: 20, carbs: 66, fat: 14, category: 'Lunch', foodCategory: 'Persian', baseGrams: 350 },
  { name: 'کلم پلو شیرازی', quantity: '۱ پرس کامل (۳۵۰ گرم)', calories: 490, protein: 22, carbs: 65, fat: 15, category: 'Lunch', foodCategory: 'Persian', baseGrams: 350 },
  { name: 'آلبالو پلو با مرغ', quantity: '۱ پرس کامل (۳۵۰ گرم)', calories: 540, protein: 30, carbs: 72, fat: 14, category: 'Lunch', foodCategory: 'Persian', baseGrams: 350 },
  { name: 'آبگوشت / دیزی سنتی', quantity: '۱ کاسه با مخلفات (۴۰۰ گرم)', calories: 620, protein: 36, carbs: 45, fat: 28, category: 'Lunch', foodCategory: 'Persian', baseGrams: 400 },
  { name: 'کشک بادمجان سنتی', quantity: '۱ بشقاب (۲۵۰ گرم)', calories: 320, protein: 10, carbs: 18, fat: 22, category: 'Dinner', foodCategory: 'Persian', baseGrams: 250 },
  { name: 'میرزا قاسمی رشتی', quantity: '۱ بشقاب (۲۵۰ گرم)', calories: 240, protein: 8, carbs: 14, fat: 16, category: 'Dinner', foodCategory: 'Persian', baseGrams: 250 },
  { name: 'کوکو سبزی خانگی', quantity: '۳ تکه (۱۵۰ گرم)', calories: 220, protein: 9, carbs: 10, fat: 16, category: 'Dinner', foodCategory: 'Persian', baseGrams: 150 },
  { name: 'کوکو سیب‌زمینی', quantity: '۲ عدد (۱۶۰ گرم)', calories: 280, protein: 7, carbs: 32, fat: 14, category: 'Dinner', foodCategory: 'Persian', baseGrams: 160 },
  { name: 'کتلت گوشت خانگی', quantity: '۳ عدد (۱۸۰ گرم)', calories: 380, protein: 22, carbs: 20, fat: 22, category: 'Dinner', foodCategory: 'Persian', baseGrams: 180 },
  { name: 'دلمه برگ مو / فلفل', quantity: '۴ عدد (۲۵۰ گرم)', calories: 320, protein: 12, carbs: 42, fat: 11, category: 'Lunch', foodCategory: 'Persian', baseGrams: 250 },
  { name: 'کوفته تبریزی', quantity: '۱ عدد بزرگ (۳۰۰ گرم)', calories: 450, protein: 28, carbs: 35, fat: 20, category: 'Lunch', foodCategory: 'Persian', baseGrams: 300 },
  { name: 'آش رشته با کشک', quantity: '۱ کاسه (۳۵۰ گرم)', calories: 340, protein: 14, carbs: 52, fat: 8, category: 'Dinner', foodCategory: 'Persian', baseGrams: 350 },
  { name: 'آش دوغ اردبیل', quantity: '۱ کاسه (۳۵۰ گرم)', calories: 260, protein: 12, carbs: 36, fat: 7, category: 'Dinner', foodCategory: 'Persian', baseGrams: 350 },
  { name: 'حلیم گندم و گوشت', quantity: '۱ کاسه (۳۰۰ گرم)', calories: 380, protein: 22, carbs: 52, fat: 8, category: 'Breakfast', foodCategory: 'Persian', baseGrams: 300 },
  { name: 'عدسی خانگی', quantity: '۱ کاسه (۲۵۰ گرم)', calories: 240, protein: 16, carbs: 38, fat: 2, category: 'Breakfast', foodCategory: 'Persian', baseGrams: 250 },
  { name: 'خوراک لوبیا چیتی با قارچ', quantity: '۱ کاسه (۲۵۰ گرم)', calories: 260, protein: 15, carbs: 42, fat: 3, category: 'Dinner', foodCategory: 'Persian', baseGrams: 250 },
  { name: 'سوپ جو پرک با مرغ', quantity: '۱ کاسه (۲۵۰ گرم)', calories: 180, protein: 12, carbs: 24, fat: 4, category: 'Dinner', foodCategory: 'Persian', baseGrams: 250 },
  { name: 'شله زرد زعفرانی', quantity: '۱ کاسه (۲۰۰ گرم)', calories: 280, protein: 3, carbs: 62, fat: 2, category: 'Snack', foodCategory: 'Persian', baseGrams: 200 },
  { name: 'فرنی نشاسته / برنج', quantity: '۱ کاسه (۲۰۰ گرم)', calories: 210, protein: 6, carbs: 36, fat: 4, category: 'Snack', foodCategory: 'Persian', baseGrams: 200 },
  { name: 'شیر برنج خانگی', quantity: '۱ کاسه (۲۵۰ گرم)', calories: 250, protein: 7, carbs: 44, fat: 5, category: 'Snack', foodCategory: 'Persian', baseGrams: 250 },

  // 🍳 صبحانه (Breakfast)
  { name: 'املت گوجه‌فرنگی', quantity: '۱ بشقاب (۲۰۰ گرم)', calories: 240, protein: 13, carbs: 8, fat: 17, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 200 },
  { name: 'املت قارچ و اسفناج', quantity: '۱ بشقاب (۲۰۰ گرم)', calories: 210, protein: 14, carbs: 6, fat: 15, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 200 },
  { name: 'تخم‌مرغ آب‌پز', quantity: '۲ عدد بزرگ (۱۱۰ گرم)', calories: 155, protein: 13, carbs: 1, fat: 11, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 110 },
  { name: 'تخم‌مرغ نیمرو با کره', quantity: '۲ عدد (۱۲۰ گرم)', calories: 195, protein: 12, carbs: 1, fat: 16, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 120 },
  { name: 'نان و پنیر سفید و گردو', quantity: '۱ لقمه کامل (۱۲۰ گرم)', calories: 290, protein: 12, carbs: 26, fat: 16, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 120 },
  { name: 'خامه و عسل', quantity: '۲ ق خامه + ۱ ق عسل (۷۰ گرم)', calories: 260, protein: 2, carbs: 22, fat: 18, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 70 },
  { name: 'کره و مربای خانگی', quantity: '۲۰گ کره + ۲ق مربا (۶۰ گرم)', calories: 230, protein: 1, carbs: 28, fat: 13, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 60 },
  { name: 'پنیر تبریزی / لیقوان', quantity: '۵۰ گرم', calories: 150, protein: 10, carbs: 1, fat: 12, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 50 },
  { name: 'سرشیر سنتی', quantity: '۵۰ گرم', calories: 195, protein: 2, carbs: 3, fat: 20, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 50 },
  { name: 'اوتمیل موز و کره بادام‌زمینی', quantity: '۱ کاسه (۲۵۰ گرم)', calories: 340, protein: 12, carbs: 52, fat: 10, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 250 },
  { name: 'نان سنگک تازه', quantity: '۱ تکه بزرگ (۱۰۰ گرم)', calories: 250, protein: 8, carbs: 50, fat: 2, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 100 },
  { name: 'نان بربری', quantity: '۱ تکه بزرگ (۱۰۰ گرم)', calories: 260, protein: 8, carbs: 52, fat: 2, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 100 },
  { name: 'نان تست سبوس‌دار جو', quantity: '۲ برش (۶۰ گرم)', calories: 140, protein: 6, carbs: 24, fat: 2, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 60 },
  { name: 'نان لواش', quantity: '۱ عدد کامل (۶۰ گرم)', calories: 160, protein: 5, carbs: 33, fat: 1, category: 'Breakfast', foodCategory: 'Breakfast', baseGrams: 60 },

  // 🍗 پروتئین‌ها (Protein)
  { name: 'سینه مرغ گریل‌شده', quantity: '۱۵۰ گرم', calories: 240, protein: 46, carbs: 0, fat: 4, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'ران مرغ پخته بدون پوست', quantity: '۱۵۰ گرم', calories: 270, protein: 35, carbs: 0, fat: 14, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'فیله ماهی سالمون گریل', quantity: '۱۵۰ گرم', calories: 310, protein: 34, carbs: 0, fat: 18, category: 'Dinner', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'ماهی قزل‌آلا کباب‌شده', quantity: '۲۰۰ گرم', calories: 290, protein: 40, carbs: 0, fat: 13, category: 'Dinner', foodCategory: 'Protein', baseGrams: 200 },
  { name: 'تن ماهی در روغن', quantity: '۱ قوطی (۱۸۰ گرم)', calories: 320, protein: 38, carbs: 0, fat: 18, category: 'Lunch', foodCategory: 'Protein', baseGrams: 180 },
  { name: 'تن ماهی در آب‌نمک', quantity: '۱ قوطی (۱۸۰ گرم)', calories: 180, protein: 40, carbs: 0, fat: 2, category: 'Lunch', foodCategory: 'Protein', baseGrams: 180 },
  { name: 'میگو گریل‌شده / پخته', quantity: '۱۵۰ گرم', calories: 150, protein: 29, carbs: 1, fat: 2, category: 'Dinner', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'فیله گوساله کبابی', quantity: '۱۵۰ گرم', calories: 280, protein: 38, carbs: 0, fat: 13, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'گوشت گوسفندی پخته', quantity: '۱۵۰ گرم', calories: 340, protein: 33, carbs: 0, fat: 22, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'گوشت چرخ‌کرده سرخ‌شده', quantity: '۱۵۰ گرم', calories: 330, protein: 31, carbs: 0, fat: 22, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'جگر مرغ / گوسفند', quantity: '۱۵۰ گرم', calories: 220, protein: 32, carbs: 2, fat: 8, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'بوقلمون کباب‌شده', quantity: '۱۵۰ گرم', calories: 220, protein: 42, carbs: 0, fat: 5, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },
  { name: 'سفیده تخم‌مرغ پخته', quantity: '۴ عدد (۱۳۰ گرم)', calories: 68, protein: 14, carbs: 1, fat: 0.2, category: 'Breakfast', foodCategory: 'Protein', baseGrams: 130 },
  { name: 'پروتئین وی (Protein Whey)', quantity: '۱ پیمانه (۳۰ گرم)', calories: 120, protein: 24, carbs: 3, fat: 1.5, category: 'Snack', foodCategory: 'Protein', baseGrams: 30 },
  { name: 'پنیر توفو سویا', quantity: '۱۵۰ گرم', calories: 120, protein: 12, carbs: 3, fat: 7, category: 'Lunch', foodCategory: 'Protein', baseGrams: 150 },

  // 🍚 برنج و غلات (Rice & Grains)
  { name: 'برنج سفید کته زعفرانی', quantity: '۱ پیمانه (۱۵۰ گرم)', calories: 205, protein: 4, carbs: 45, fat: 0.5, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 150 },
  { name: 'برنج سفید آبکش', quantity: '۱ پیمانه (۱۵۰ گرم)', calories: 190, protein: 3.8, carbs: 42, fat: 0.3, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 150 },
  { name: 'برنج قهوه‌ای پخته', quantity: '۱ پیمانه (۱۵۰ گرم)', calories: 215, protein: 5, carbs: 45, fat: 1.8, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 150 },
  { name: 'ماکارونی با سس گوشت', quantity: '۱ بشقاب (۳۰۰ گرم)', calories: 480, protein: 22, carbs: 68, fat: 14, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 300 },
  { name: 'پاستا آلفردو با مرغ', quantity: '۱ بشقاب (۳۵۰ گرم)', calories: 580, protein: 28, carbs: 52, fat: 28, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 350 },
  { name: 'سیب‌زمینی آب‌پز', quantity: '۱ عدد بزرگ (۲۰۰ گرم)', calories: 170, protein: 4, carbs: 38, fat: 0.2, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 200 },
  { name: 'سیب‌زمینی سرخ‌شده / تنوری', quantity: '۱۵۰ گرم', calories: 310, protein: 4, carbs: 42, fat: 14, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 150 },
  { name: 'کینوا پخته‌شده', quantity: '۱ پیمانه (۱۵۰ گرم)', calories: 180, protein: 6, carbs: 32, fat: 3, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 150 },
  { name: 'جو دوسر / اوتمیل پخته', quantity: '۱ پیمانه (۲۰۰ گرم)', calories: 160, protein: 6, carbs: 28, fat: 2.5, category: 'Breakfast', foodCategory: 'RiceGrains', baseGrams: 200 },
  { name: 'نخود پخته', quantity: '۱ پیمانه (۱۵۰ گرم)', calories: 240, protein: 13, carbs: 40, fat: 4, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 150 },
  { name: 'عدس پخته', quantity: '۱ پیمانه (۱۵۰ گرم)', calories: 175, protein: 13, carbs: 30, fat: 0.8, category: 'Lunch', foodCategory: 'RiceGrains', baseGrams: 150 },
  { name: 'نان لواش', quantity: '۱ نان کامل (۷۰ گرم)', calories: 190, protein: 6, carbs: 40, fat: 1, category: 'Breakfast', foodCategory: 'RiceGrains', baseGrams: 70 },
  { name: 'نان تافتون', quantity: '۱ نان کامل (۱۰۰ گرم)', calories: 260, protein: 8, carbs: 52, fat: 2, category: 'Breakfast', foodCategory: 'RiceGrains', baseGrams: 100 },

  // 🥗 سبزیجات (Vegetables)
  { name: 'سالاد شیرازی با آبغوره', quantity: '۱ کاسه (۲۰۰ گرم)', calories: 50, protein: 1.5, carbs: 9, fat: 1, category: 'Lunch', foodCategory: 'Vegetables', baseGrams: 200 },
  { name: 'سالاد فصل با سس', quantity: '۱ بشقاب (۲۵۰ گرم)', calories: 160, protein: 3, carbs: 12, fat: 12, category: 'Lunch', foodCategory: 'Vegetables', baseGrams: 250 },
  { name: 'سبزی خوردن تازه', quantity: '۱ بشقاب (۱۰۰ گرم)', calories: 25, protein: 2, carbs: 4, fat: 0.3, category: 'Lunch', foodCategory: 'Vegetables', baseGrams: 100 },
  { name: 'کلم بروکلی بخارپز', quantity: '۱۵۰ گرم', calories: 50, protein: 4, carbs: 10, fat: 0.6, category: 'Dinner', foodCategory: 'Vegetables', baseGrams: 150 },
  { name: 'قارچ کبابی / بخارپز', quantity: '۱۵۰ گرم', calories: 35, protein: 4.5, carbs: 5, fat: 0.5, category: 'Dinner', foodCategory: 'Vegetables', baseGrams: 150 },
  { name: 'اسفناج پخته / نرگسی', quantity: '۱۵۰ گرم', calories: 65, protein: 6, carbs: 6, fat: 2.5, category: 'Dinner', foodCategory: 'Vegetables', baseGrams: 150 },
  { name: 'کدو سبز پخته', quantity: '۱۵۰ گرم', calories: 30, protein: 2, carbs: 5, fat: 0.3, category: 'Dinner', foodCategory: 'Vegetables', baseGrams: 150 },
  { name: 'بادمجان کبابی', quantity: '۱۵۰ گرم', calories: 45, protein: 1.5, carbs: 9, fat: 0.5, category: 'Dinner', foodCategory: 'Vegetables', baseGrams: 150 },
  { name: 'لبو / چغندر پخته', quantity: '۱۵۰ گرم', calories: 65, protein: 2.5, carbs: 14, fat: 0.3, category: 'Snack', foodCategory: 'Vegetables', baseGrams: 150 },
  { name: 'ذرت شیرین بخارپز', quantity: '۱۰۰ گرم', calories: 90, protein: 3, carbs: 19, fat: 1.2, category: 'Snack', foodCategory: 'Vegetables', baseGrams: 100 },

  // 🥛 لبنیات (Dairy)
  { name: 'ماست یونانی پرپروتئین', quantity: '۲۰۰ گرم', calories: 150, protein: 18, carbs: 6, fat: 4, category: 'Snack', foodCategory: 'Dairy', baseGrams: 200 },
  { name: 'ماست کم‌چرب', quantity: '۱ لیوان (۲۴۰ گرم)', calories: 120, protein: 9, carbs: 12, fat: 3, category: 'Snack', foodCategory: 'Dairy', baseGrams: 240 },
  { name: 'ماست پرچرب / چکیده', quantity: '۱۵۰ گرم', calories: 180, protein: 8, carbs: 8, fat: 12, category: 'Snack', foodCategory: 'Dairy', baseGrams: 150 },
  { name: 'ماست و خیار خانگی', quantity: '۱ کاسه (۲۰۰ گرم)', calories: 110, protein: 6, carbs: 10, fat: 4, category: 'Snack', foodCategory: 'Dairy', baseGrams: 200 },
  { name: 'ماست و موسیر', quantity: '۱ کاسه (۱۵۰ گرم)', calories: 130, protein: 6, carbs: 9, fat: 7, category: 'Snack', foodCategory: 'Dairy', baseGrams: 150 },
  { name: 'دوغ نعنایی کم‌چرب', quantity: '۱ لیوان (۲۵۰ میلی)', calories: 55, protein: 3, carbs: 5, fat: 2, category: 'Lunch', foodCategory: 'Dairy', baseGrams: 250 },
  { name: 'شیر کم‌چرب', quantity: '۱ لیوان (۲۴۰ میلی)', calories: 100, protein: 8, carbs: 12, fat: 2, category: 'Breakfast', foodCategory: 'Dairy', baseGrams: 240 },
  { name: 'شیر پرچرب / کامل', quantity: '۱ لیوان (۲۴۰ میلی)', calories: 150, protein: 8, carbs: 12, fat: 8, category: 'Breakfast', foodCategory: 'Dairy', baseGrams: 240 },
  { name: 'کشک پاستوریزه', quantity: '۲ قاشق (۵۰ گرم)', calories: 80, protein: 5, carbs: 5, fat: 4, category: 'Lunch', foodCategory: 'Dairy', baseGrams: 50 },
  { name: 'پنیر سفید فتا', quantity: '۵۰ گرم', calories: 135, protein: 8, carbs: 2, fat: 11, category: 'Breakfast', foodCategory: 'Dairy', baseGrams: 50 },

  // 🥜 مغزها و آجیل (Nuts)
  { name: 'گردو تازه / خشک', quantity: '۵ عدد (۳۰ گرم)', calories: 196, protein: 4.5, carbs: 3.8, fat: 19.5, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'بادام درختی خام', quantity: 'یک مشت (۳۰ گرم)', calories: 170, protein: 6, carbs: 6, fat: 15, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'پسته رفسنجان', quantity: '۳۰ گرم', calories: 172, protein: 6, carbs: 8, fat: 14, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'بادام هندی', quantity: '۳۰ گرم', calories: 165, protein: 5, carbs: 9, fat: 13, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'فندق', quantity: '۳۰ گرم', calories: 188, protein: 4.2, carbs: 5, fat: 18, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'تخمه آفتابگردان', quantity: '۳۰ گرم', calories: 175, protein: 6, carbs: 6, fat: 15, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'تخمه کدو مرمری', quantity: '۳۰ گرم', calories: 170, protein: 8.5, carbs: 4, fat: 14, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'کره بادام‌زمینی طبیعی', quantity: '۲ قاشق (۳۰ گرم)', calories: 188, protein: 8, carbs: 7, fat: 16, category: 'Snack', foodCategory: 'Nuts', baseGrams: 30 },
  { name: 'کنجد بو داده', quantity: '۱ قاشق (۱۵ گرم)', calories: 88, protein: 2.6, carbs: 3.5, fat: 7.4, category: 'Snack', foodCategory: 'Nuts', baseGrams: 15 },

  // ☕ نوشیدنی‌ها (Drinks)
  { name: 'چای سیاه / سبز بدون قند', quantity: '۱ فنجان (۲۰۰ میلی)', calories: 2, protein: 0, carbs: 0, fat: 0, category: 'Snack', foodCategory: 'Drinks', baseGrams: 200 },
  { name: 'قهوه اسپرسو / بلک', quantity: '۱ شات (۳۰ میلی)', calories: 5, protein: 0, carbs: 1, fat: 0, category: 'Breakfast', foodCategory: 'Drinks', baseGrams: 30 },
  { name: 'کاپوچینو بدون شکر', quantity: '۱ فنجان (۱۵۰ میلی)', calories: 80, protein: 4, carbs: 6, fat: 4, category: 'Breakfast', foodCategory: 'Drinks', baseGrams: 150 },
  { name: 'آیس لاته کم‌چرب', quantity: '۱ لیوان (۲۵۰ میلی)', calories: 110, protein: 6, carbs: 12, fat: 3, category: 'Breakfast', foodCategory: 'Drinks', baseGrams: 250 },
  { name: 'آب پرتقال طبیعی', quantity: '۱ لیوان (۲۴۰ میلی)', calories: 112, protein: 2, carbs: 26, fat: 0, category: 'Breakfast', foodCategory: 'Drinks', baseGrams: 240 },
  { name: 'آب هویج تازه', quantity: '۱ لیوان (۲۴۰ میلی)', calories: 95, protein: 2, carbs: 22, fat: 0.4, category: 'Breakfast', foodCategory: 'Drinks', baseGrams: 240 },
  { name: 'آب سیب طبیعی', quantity: '۱ لیوان (۲۴۰ میلی)', calories: 115, protein: 0.3, carbs: 28, fat: 0.3, category: 'Snack', foodCategory: 'Drinks', baseGrams: 240 },
  { name: 'شربت آبلیمو عسل', quantity: '۱ لیوان (۲۵۰ میلی)', calories: 90, protein: 0.2, carbs: 24, fat: 0, category: 'Snack', foodCategory: 'Drinks', baseGrams: 250 },
  { name: 'عرقیات سنتی (کاسنی/بیدمشک)', quantity: '۱ لیوان (۲۵۰ میلی)', calories: 15, protein: 0, carbs: 3, fat: 0, category: 'Snack', foodCategory: 'Drinks', baseGrams: 250 },
  { name: 'اسموتی پروتئینی موز و شیر', quantity: '۱ لیوان (۳۰۰ میلی)', calories: 280, protein: 10, carbs: 50, fat: 5, category: 'Snack', foodCategory: 'Drinks', baseGrams: 300 }
];

export default function NutritionModule({ record, onUpdateRecord, currentDate, onSetDate }: NutritionProps) {
  const { lang, t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'meals' | 'quick_add' | 'custom_add' | 'assistant' | 'report'>('meals');
  
  // Date Helpers
  const getReadableDate = (dateStr: string) => {
    if (!dateStr) return '';
    return formatGregorianDate(dateStr, lang, 'long');
  };

  const handlePrevDay = () => {
    if (onSetDate && currentDate) {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 1);
      onSetDate(d.toISOString().split('T')[0]);
    }
  };

  const handleNextDay = () => {
    if (onSetDate && currentDate) {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 1);
      onSetDate(d.toISOString().split('T')[0]);
    }
  };

  const handleToday = () => {
    if (onSetDate) {
      onSetDate(new Date().toISOString().split('T')[0]);
    }
  };

  // User App State & Goals
  const appState = loadAppState();
  const targetCalories = appState.settings?.targetCalories || 2200;
  const targetWater = appState.settings?.targetWater || 2000;
  // Estimate macros targets from calories if not set
  const targetProtein = Math.round((targetCalories * 0.25) / 4); // 25% protein
  const targetCarbs = Math.round((targetCalories * 0.50) / 4);   // 50% carbs
  const targetFat = Math.round((targetCalories * 0.25) / 9);     // 25% fat

  // Calculate Today's Totals
  const foods = record.foods || [];
  const totalCalories = foods.reduce((sum, f) => sum + (f.calories || 0), 0);
  const totalProtein = foods.reduce((sum, f) => sum + (f.protein || 0), 0);
  const totalCarbs = foods.reduce((sum, f) => sum + (f.carbs || 0), 0);
  const totalFat = foods.reduce((sum, f) => sum + (f.fat || 0), 0);

  // Sync D1 nutrition entries when currentDate changes
  useEffect(() => {
    if (!currentDate) return;
    let isMounted = true;
    apiGet('/api/nutrition', { date: currentDate })
      .then((res) => {
        if (!isMounted) return;
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          const cloudFoods: FoodItem[] = res.data.map((item: any) => ({
            id: item.id,
            name: item.food_name,
            category: (item.meal_type as any) || 'Lunch',
            quantity: item.portion || '۱ وعده',
            calories: Number(item.calories || 0),
            protein: Number(item.protein_g || 0),
            carbs: Number(item.carbs_g || 0),
            fat: Number(item.fat_g || 0),
            time: item.created_at ? new Date(item.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }) : undefined
          }));

          const existingIds = new Set((record.foods || []).map((f) => f.id));
          const missing = cloudFoods.filter((cf) => !existingIds.has(cf.id));
          if (missing.length > 0) {
            onUpdateRecord({
              ...record,
              foods: [...(record.foods || []), ...missing]
            });
          }
        }
      })
      .catch((err) => {
        console.warn('Failed querying nutrition_entries from D1:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [currentDate]);

  // Favorites & Recently Used state
  const [favoriteNames, setFavoriteNames] = useState<string[]>(['تخم‌مرغ آب‌پز', 'سینه مرغ گریل‌شده', 'برنج سفید کته']);

  const toggleFavorite = (foodName: string) => {
    let next: string[];
    if (favoriteNames.includes(foodName)) {
      next = favoriteNames.filter(n => n !== foodName);
    } else {
      next = [...favoriteNames, foodName];
    }
    setFavoriteNames(next);
  };

  // Quick Add Filter states
  const [quickFoodCategory, setQuickFoodCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showOnlyFavs, setShowOnlyFavs] = useState<boolean>(false);

  // Selected Food Logging Modal Sheet state
  const [selectedTemplate, setSelectedTemplate] = useState<FoodTemplate | null>(null);
  const [selectedMealType, setSelectedMealType] = useState<'Breakfast' | 'Lunch' | 'Dinner' | 'Snack'>('Breakfast');
  const [servingMultiplier, setServingMultiplier] = useState<number>(1);
  const [customGramAmount, setCustomGramAmount] = useState<string>('');
  const [isAiCalculatingSheet, setIsAiCalculatingSheet] = useState<boolean>(false);
  const [aiNutrientResult, setAiNutrientResult] = useState<{
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    quantity: string;
    source: 'base' | 'multiplier' | 'grams' | 'ai';
  } | null>(null);

  // Open Log Food Sheet
  const handleOpenFoodSheet = (template: FoodTemplate, defaultMeal?: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack') => {
    setSelectedTemplate(template);
    setSelectedMealType(defaultMeal || template.category);
    setServingMultiplier(1);
    const baseG = template.baseGrams || 100;
    setCustomGramAmount(String(baseG));
    setAiNutrientResult({
      calories: template.calories,
      protein: template.protein,
      carbs: template.carbs,
      fat: template.fat,
      quantity: template.quantity,
      source: 'base'
    });
  };

  // Helper to compute live nutrients dynamically from current input
  const getComputedNutrients = () => {
    if (!selectedTemplate) return { calories: 0, protein: 0, carbs: 0, fat: 0, quantity: '', source: 'base' as const };

    if (aiNutrientResult && aiNutrientResult.source === 'ai') {
      return aiNutrientResult;
    }

    if (customGramAmount.trim() && !isNaN(Number(customGramAmount))) {
      const grams = Number(customGramAmount);
      const baseG = selectedTemplate.baseGrams || 100;
      const factor = grams / baseG;
      return {
        calories: Math.round(selectedTemplate.calories * factor),
        protein: Math.round(selectedTemplate.protein * factor),
        carbs: Math.round(selectedTemplate.carbs * factor),
        fat: Math.round(selectedTemplate.fat * factor),
        quantity: `${grams} گرم`,
        source: 'grams' as const
      };
    }

    return {
      calories: Math.round(selectedTemplate.calories * servingMultiplier),
      protein: Math.round(selectedTemplate.protein * servingMultiplier),
      carbs: Math.round(selectedTemplate.carbs * servingMultiplier),
      fat: Math.round(selectedTemplate.fat * servingMultiplier),
      quantity: servingMultiplier !== 1 ? `${servingMultiplier} برابر (${selectedTemplate.quantity})` : selectedTemplate.quantity,
      source: 'multiplier' as const
    };
  };

  // Calculate with AI for exact weight/portion in Modal Sheet
  const handleAiRecalculateSheet = async () => {
    if (!selectedTemplate) return;
    setIsAiCalculatingSheet(true);

    const targetQtyStr = customGramAmount.trim() && !isNaN(Number(customGramAmount))
      ? `${customGramAmount} گرم`
      : `${servingMultiplier} برابر (${selectedTemplate.quantity})`;

    try {
      const prompt = buildNutritionPrompt(selectedTemplate.name, targetQtyStr);
      const aiReply = await sendWorkerAiRequest('nutrition', prompt);
      const parsed = parseNutritionReply(aiReply);

      if (parsed && parsed.calories > 0) {
        setAiNutrientResult({
          calories: Math.round(parsed.calories),
          protein: Math.round(parsed.protein || 0),
          carbs: Math.round(parsed.carbs || 0),
          fat: Math.round(parsed.fat || 0),
          quantity: parsed.quantity || targetQtyStr,
          source: 'ai'
        });
      } else {
        const fallback = getComputedNutrients();
        setAiNutrientResult({
          ...fallback,
          source: 'grams'
        });
      }
    } catch (err) {
      console.warn('AI sheet calculation error:', err);
      const fallback = getComputedNutrients();
      setAiNutrientResult({
        ...fallback,
        source: 'grams'
      });
    } finally {
      setIsAiCalculatingSheet(false);
    }
  };

  // Confirm Log Food from Sheet
  const handleConfirmAddFood = () => {
    if (!selectedTemplate) return;

    const computed = getComputedNutrients();
    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    const newFoodItem: FoodItem = {
      id: 'food-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
      name: selectedTemplate.name,
      category: selectedMealType,
      quantity: computed.quantity,
      calories: computed.calories,
      protein: computed.protein,
      carbs: computed.carbs,
      fat: computed.fat,
      time: timeStr
    };

    onUpdateRecord({
      ...record,
      foods: [...foods, newFoodItem]
    });

    if (currentDate) {
      apiPost('/api/nutrition', {
        id: newFoodItem.id,
        date: currentDate,
        meal_type: newFoodItem.category,
        food_name: newFoodItem.name,
        portion: newFoodItem.quantity,
        calories: newFoodItem.calories,
        protein_g: newFoodItem.protein || 0,
        carbs_g: newFoodItem.carbs || 0,
        fat_g: newFoodItem.fat || 0
      }).catch((err) => console.warn('Failed saving to nutrition_entries:', err));
    }

    setSelectedTemplate(null);
    setAiNutrientResult(null);
  };

  // Custom Add Form state
  const [customName, setCustomName] = useState('');
  const [customQty, setCustomQty] = useState('');
  const [customCal, setCustomCal] = useState('');
  const [customProt, setCustomProt] = useState('');
  const [customCarb, setCustomCarb] = useState('');
  const [customFat, setCustomFat] = useState('');
  const [customCategory, setCustomCategory] = useState<'Breakfast' | 'Lunch' | 'Dinner' | 'Snack'>('Lunch');
  const [isAiEstimating, setIsAiEstimating] = useState(false);

  // AI Auto-Estimate Custom Food
  const handleAiEstimateCustom = async () => {
    if (!customName.trim()) return;
    setIsAiEstimating(true);
    try {
      const prompt = buildNutritionPrompt(customName.trim(), customQty.trim() || '۱ پرس');
      const aiReply = await sendWorkerAiRequest('nutrition', prompt);
      const parsed = parseNutritionReply(aiReply);
      
      let cal = parsed.calories || 0;
      let prot = parsed.protein || 0;
      let carb = parsed.carbs || 0;
      let fat = parsed.fat || 0;

      if (cal <= 0) {
        const est = estimateLocalNutrients(customName.trim(), customQty.trim() || '۱ پرس');
        cal = est.calories;
        prot = est.protein;
        carb = est.carbs;
        fat = est.fat;
      }

      setCustomCal(String(cal));
      setCustomProt(String(prot));
      setCustomCarb(String(carb));
      setCustomFat(String(fat));
      if (parsed.quantity) setCustomQty(parsed.quantity);
    } catch (e) {
      const est = estimateLocalNutrients(customName.trim(), customQty.trim() || '۱ پرس');
      setCustomCal(String(est.calories));
      setCustomProt(String(est.protein));
      setCustomCarb(String(est.carbs));
      setCustomFat(String(est.fat));
    } finally {
      setIsAiEstimating(false);
    }
  };

  // Submit Custom Food
  const handleSaveCustomFood = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customName.trim()) return;

    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    const newFoodItem: FoodItem = {
      id: 'food-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
      name: customName.trim(),
      category: customCategory,
      quantity: customQty.trim() || '۱ سروینگ',
      calories: Number(customCal) || 0,
      protein: Number(customProt) || 0,
      carbs: Number(customCarb) || 0,
      fat: Number(customFat) || 0,
      time: timeStr
    };

    onUpdateRecord({
      ...record,
      foods: [...foods, newFoodItem]
    });

    if (currentDate) {
      apiPost('/api/nutrition', {
        id: newFoodItem.id,
        date: currentDate,
        meal_type: newFoodItem.category,
        food_name: newFoodItem.name,
        portion: newFoodItem.quantity,
        calories: newFoodItem.calories,
        protein_g: newFoodItem.protein || 0,
        carbs_g: newFoodItem.carbs || 0,
        fat_g: newFoodItem.fat || 0
      }).catch((err) => console.warn('Failed saving custom food to nutrition_entries:', err));
    }

    // Reset Form
    setCustomName('');
    setCustomQty('');
    setCustomCal('');
    setCustomProt('');
    setCustomCarb('');
    setCustomFat('');
    setActiveTab('meals');
  };

  // Delete Food item
  const handleDeleteFood = (id: string) => {
    const updated = foods.filter(f => f.id !== id);
    onUpdateRecord({
      ...record,
      foods: updated
    });
    apiDelete(`/api/nutrition/${id}`).catch((err) => console.warn('Failed deleting from nutrition_entries:', err));
  };

  // Water Intake Actions
  const handleWaterAdd = (amount: number) => {
    const currentWater = record.waterIntake || 0;
    onUpdateRecord({
      ...record,
      waterIntake: Math.max(0, currentWater + amount)
    });
  };

  // Filter Quick Foods list
  const filteredQuickFoods = useMemo(() => {
    return EXTENDED_FOOD_TEMPLATES.filter(f => {
      if (showOnlyFavs && !favoriteNames.includes(f.name)) return false;
      if (quickFoodCategory !== 'all' && f.foodCategory !== quickFoodCategory) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const loc = getLocalizedFoodItem(f.name, f.quantity, lang);
        return f.name.toLowerCase().includes(q) || 
               f.quantity.toLowerCase().includes(q) ||
               loc.name.toLowerCase().includes(q) ||
               loc.quantity.toLowerCase().includes(q);
      }
      return true;
    });
  }, [quickFoodCategory, searchQuery, showOnlyFavs, favoriteNames, lang]);

  const isToday = currentDate === new Date().toISOString().split('T')[0];
  const dir = lang === 'fa' ? 'rtl' : 'ltr';

  // Directional icons: in RTL, previous is ChevronRight, next is ChevronLeft; vice versa in LTR
  const PrevIcon = dir === 'rtl' ? ChevronRight : ChevronLeft;
  const NextIcon = dir === 'rtl' ? ChevronLeft : ChevronRight;

  return (
    <div className="space-y-5 pb-24 md:pb-8">
      {/* Date Header Navigator */}
      <div className="bg-white dark:bg-gray-900 rounded-3xl p-4 sm:p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
        {/* Top Title & Quick Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 shrink-0">
              <Apple className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black font-display text-gray-900 dark:text-white flex items-center gap-2">
                <span>{t('nutriHeaderTitle', 'مدیریت هوشمند تغذیه و کالری')}</span>
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('nutriSubTitle', 'ثبت وعده‌ها، محاسبات ریزمغذی‌ها و تحلیل مستقیم با Cloudflare D1')}
              </p>
            </div>
          </div>

          {/* Today Button / Status Badge */}
          {!isToday ? (
            <button
              onClick={handleToday}
              className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100/80 dark:bg-emerald-950/80 hover:bg-emerald-200 dark:hover:bg-emerald-900 transition-all cursor-pointer border border-emerald-300/40 dark:border-emerald-700/50 shadow-xs active:scale-95"
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>{t('nutriBackToToday', 'بازگشت به امروز')}</span>
            </button>
          ) : (
            <span className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800">
              <Calendar className="w-3.5 h-3.5" />
              <span>{t('nutriToday', 'امروز')}</span>
            </span>
          )}
        </div>

        {/* Responsive Date Selector Toolbar */}
        <div className="flex items-center justify-between gap-2 bg-gray-50/80 dark:bg-gray-800/50 p-2 sm:p-2.5 rounded-2xl border border-gray-200/70 dark:border-gray-700/60">
          {/* Previous Day Button */}
          <button
            onClick={handlePrevDay}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-gray-700 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all border border-gray-200/80 dark:border-gray-700 cursor-pointer shadow-2xs active:scale-95 shrink-0"
            title={t('nutriPrevDay', 'روز قبل')}
          >
            <PrevIcon className="w-4 h-4 shrink-0" />
            <span className="hidden xs:inline">{t('nutriPrevDay', 'روز قبل')}</span>
          </button>

          {/* Centered Readable Date + Native Date Picker */}
          <div className="flex items-center justify-center gap-2 flex-1 min-w-0 text-center">
            <div className="relative inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700 shadow-2xs group hover:border-emerald-500/50 transition-colors max-w-full">
              <Calendar className="w-4 h-4 text-emerald-500 shrink-0" />
              <span className="text-xs sm:text-sm font-black text-gray-800 dark:text-gray-100 truncate">
                {getReadableDate(currentDate || new Date().toISOString().split('T')[0])}
              </span>
              <input
                type="date"
                value={currentDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => onSetDate?.(e.target.value)}
                aria-label={t('nutriSelectDate', 'انتخاب تاریخ')}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
            </div>
          </div>

          {/* Next Day Button */}
          <button
            onClick={handleNextDay}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-gray-700 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all border border-gray-200/80 dark:border-gray-700 cursor-pointer shadow-2xs active:scale-95 shrink-0"
            title={t('nutriNextDay', 'روز بعد')}
          >
            <span className="hidden xs:inline">{t('nutriNextDay', 'روز بعد')}</span>
            <NextIcon className="w-4 h-4 shrink-0" />
          </button>
        </div>
      </div>

      {/* TODAY'S NUTRITION SUMMARY CARD (REQUIREMENT #3) */}
      <div className="bg-gradient-to-br from-emerald-900 via-teal-900 to-gray-900 text-white rounded-3xl p-5 sm:p-6 shadow-xl relative overflow-hidden space-y-5">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            <h2 className="text-base sm:text-lg font-bold font-display">{t('nutriSummaryTitle', 'خلاصه ارزش غذایی امروز')}</h2>
          </div>

          <div className="flex items-center gap-2 text-xs text-emerald-200 bg-white/10 px-3 py-1 rounded-full backdrop-blur-md">
            <span>{t('nutriMealCount', 'تعداد وعده:')}</span>
            <strong className="text-white">{toPersianDigits(foods.length)}</strong>
          </div>
        </div>

        {/* 4 Core Macros Progress Bars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 1. Calories */}
          <MacroProgressCard
            title={t('nutriCalories', 'کالری مصرفی')}
            icon="🔥"
            current={totalCalories}
            target={targetCalories}
            unit={t('nutriKcal', 'کیلوکالری')}
            color="from-amber-500 to-orange-500"
            bgColor="bg-amber-500/20"
          />

          {/* 2. Protein */}
          <MacroProgressCard
            title={t('nutriProtein', 'پروتئین')}
            icon="🍗"
            current={totalProtein}
            target={targetProtein}
            unit={t('nutriGrams', 'گرم')}
            color="from-rose-500 to-pink-500"
            bgColor="bg-rose-500/20"
          />

          {/* 3. Carbohydrates */}
          <MacroProgressCard
            title={t('nutriCarbs', 'کربوهیدرات')}
            icon="🌾"
            current={totalCarbs}
            target={targetCarbs}
            unit={t('nutriGrams', 'گرم')}
            color="from-blue-500 to-indigo-500"
            bgColor="bg-blue-500/20"
          />

          {/* 4. Healthy Fats */}
          <MacroProgressCard
            title={t('nutriFat', 'چربی‌های مفید')}
            icon="🥑"
            current={totalFat}
            target={targetFat}
            unit={t('nutriGrams', 'گرم')}
            color="from-emerald-400 to-teal-500"
            bgColor="bg-emerald-500/20"
          />
        </div>

        {/* Water Intake Sub-Bar */}
        <div className="pt-3 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <Droplets className="w-4 h-4 text-cyan-400 shrink-0" />
            <span>{t('nutriWater', 'میزان آب مصرفی:')}</span>
            <strong className="text-cyan-200 text-sm font-bold">{formatNumberByLang(record.waterIntake || 0, lang)} ml</strong>
            <span className="text-gray-400 text-[11px]">{t('nutriWaterTarget', '(هدف: {{target}} ml)', { target: formatNumberByLang(targetWater, lang) })}</span>
          </div>

          <div className="flex items-center gap-1.5 self-end sm:self-auto">
            <button
              onClick={() => handleWaterAdd(250)}
              className="px-3 py-1.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-200 font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-sm active:scale-95"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>{t('nutriGlassWater', '۱ لیوان (+۲۵۰ml)')}</span>
            </button>
            <button
              onClick={() => handleWaterAdd(-250)}
              className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white font-bold text-xs transition-all cursor-pointer flex items-center justify-center active:scale-95"
              title={t('nutriReduceWater', 'کاهش آب (-۲۵۰ml)')}
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE-FIRST SECTION TABS (REQUIREMENT #4) */}
      <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800/80 p-1.5 rounded-2xl border border-gray-200 dark:border-gray-700/60 overflow-x-auto">
        <button
          onClick={() => setActiveTab('meals')}
          className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            activeTab === 'meals'
              ? 'bg-white dark:bg-gray-900 text-emerald-600 dark:text-emerald-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Utensils className="w-4 h-4" />
          <span>{t('nutriTodayMeals', 'وعده‌های امروز')}</span>
        </button>

        <button
          onClick={() => setActiveTab('quick_add')}
          className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            activeTab === 'quick_add'
              ? 'bg-white dark:bg-gray-900 text-emerald-600 dark:text-emerald-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Zap className="w-4 h-4" />
          <span>{t('nutriQuickAdd', 'افزودن سریع')}</span>
        </button>

        <button
          onClick={() => setActiveTab('custom_add')}
          className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            activeTab === 'custom_add'
              ? 'bg-white dark:bg-gray-900 text-emerald-600 dark:text-emerald-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Plus className="w-4 h-4" />
          <span>{t('nutriAiAnalysis', 'غذا/تحلیل AI')}</span>
        </button>

        <button
          onClick={() => setActiveTab('assistant')}
          className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            activeTab === 'assistant'
              ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Sparkles className="w-4 h-4 text-indigo-500" />
          <span>{t('nutriChatAssistant', 'دستیار چت')}</span>
        </button>

        <button
          onClick={() => setActiveTab('report')}
          className={`flex-1 min-w-[90px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            activeTab === 'report'
              ? 'bg-white dark:bg-gray-900 text-emerald-600 dark:text-emerald-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>{t('nutriReport', 'گزارش')}</span>
        </button>
      </div>

      {/* SECTION 1: TODAY'S MEALS BREAKDOWN */}
      {activeTab === 'meals' && (
        <div className="space-y-4">
          {(['Breakfast', 'Lunch', 'Dinner', 'Snack'] as const).map(mealCat => {
            const mealFoods = foods.filter(f => f.category === mealCat);
            const mealCal = mealFoods.reduce((s, f) => s + (f.calories || 0), 0);
            const mealProt = mealFoods.reduce((s, f) => s + (f.protein || 0), 0);
            const mealCarb = mealFoods.reduce((s, f) => s + (f.carbs || 0), 0);
            const mealFat = mealFoods.reduce((s, f) => s + (f.fat || 0), 0);

            let titleFa = t('nutriBreakfast', 'صبحانه');
            let icon = '🍳';
            if (mealCat === 'Lunch') { titleFa = t('nutriLunch', 'ناهار'); icon = '🍗'; }
            if (mealCat === 'Dinner') { titleFa = t('nutriDinner', 'شام'); icon = '🍲'; }
            if (mealCat === 'Snack') { titleFa = t('nutriSnack', 'میان‌وعده'); icon = '🍎'; }

            return (
              <div 
                key={mealCat}
                className="bg-white dark:bg-gray-900 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-3"
              >
                <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{icon}</span>
                    <h3 className="text-base font-extrabold font-display text-gray-900 dark:text-white">
                      {titleFa}
                    </h3>
                    <span className="text-xs text-gray-400">({toPersianDigits(mealFoods.length)} {t('nutriItem', 'مورد')})</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-xs text-gray-500 font-mono">
                      🔥 <strong className="text-gray-900 dark:text-white">{toPersianDigits(mealCal)}</strong> kcal
                    </div>
                    <button
                      onClick={() => {
                        setActiveTab('quick_add');
                        setQuickFoodCategory(mealCat === 'Snack' ? 'Fruits' : mealCat);
                      }}
                      className="px-2.5 py-1 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-bold text-xs hover:bg-emerald-100 transition-colors flex items-center gap-1 cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>{t('nutriAdd', 'افزودن')}</span>
                    </button>
                  </div>
                </div>

                {/* Sub-Macro pill for this meal */}
                {mealFoods.length > 0 && (
                  <div className="flex items-center gap-4 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800/40 p-2 rounded-xl">
                    <span>{t('nutriProtShort', 'پروتئین:')} <strong className="text-rose-600 dark:text-rose-400">{toPersianDigits(mealProt)}g</strong></span>
                    <span>{t('nutriCarbShort', 'کربوهیدرات:')} <strong className="text-blue-600 dark:text-blue-400">{toPersianDigits(mealCarb)}g</strong></span>
                    <span>{t('nutriFatShort', 'چربی:')} <strong className="text-emerald-600 dark:text-emerald-400">{toPersianDigits(mealFat)}g</strong></span>
                  </div>
                )}

                {/* Meal Items */}
                {mealFoods.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-3">
                    {t('nutriNoFoodForMeal', 'هیچ خوراکی برای این وعده ثبت نشده است.')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {mealFoods.map(f => {
                      const locFood = getLocalizedFoodItem(f.name, f.quantity, lang);
                      return (
                        <div 
                          key={f.id}
                          className="flex items-center justify-between p-3 rounded-2xl bg-gray-50/70 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800/80"
                        >
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">{locFood.name}</span>
                              <span className="text-[10px] px-2 py-0.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{locFood.quantity}</span>
                              {f.time && <span className="text-[10px] text-gray-400 font-mono">🕒 {f.time}</span>}
                            </div>
                            <div className="text-[11px] text-gray-500 flex items-center gap-3 font-mono">
                              <span>🔥 {formatNumberByLang(f.calories, lang)} kcal</span>
                              <span>P: {formatNumberByLang(f.protein, lang)}g</span>
                              <span>C: {formatNumberByLang(f.carbs, lang)}g</span>
                              <span>F: {formatNumberByLang(f.fat, lang)}g</span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleDeleteFood(f.id)}
                            className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg transition-colors cursor-pointer"
                            title={t('nutriDeleteFood', 'حذف خوراکی')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* SECTION 2: QUICK ADD FOOD WITH 9 CATEGORIES (REQUIREMENT #5) */}
      {activeTab === 'quick_add' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-4 sm:p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
            {/* Search and Favorite toggle */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('nutriSearchPlaceholder', 'جستجوی خوراکی (مانند: سینه مرغ، برنج، تخم مرغ...)')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 pl-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowOnlyFavs(!showOnlyFavs)}
                className={`px-4 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shrink-0 ${
                  showOnlyFavs
                    ? 'bg-amber-400 text-gray-950 shadow-md'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                <Star className={`w-4 h-4 ${showOnlyFavs ? 'fill-gray-950' : 'text-amber-500'}`} />
                <span>{t('nutriFavorites', 'علاقه‌مندی‌ها ({{count}})', { count: toPersianDigits(favoriteNames.length) })}</span>
              </button>
            </div>

            {/* 9 Categories Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {getQuickFoodCategories(t).map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setQuickFoodCategory(cat.id)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
                    quickFoodCategory === cat.id
                      ? 'bg-emerald-600 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                  }`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>

            {/* Foods Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2">
              {filteredQuickFoods.map((item, idx) => {
                const isFav = favoriteNames.includes(item.name);
                const locFood = getLocalizedFoodItem(item.name, item.quantity, lang);

                return (
                  <div
                    key={idx}
                    className="p-3.5 rounded-2xl bg-gray-50/80 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 hover:border-emerald-500/50 transition-all flex flex-col justify-between space-y-3 group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">{locFood.name}</h4>
                        <span className="text-[11px] text-gray-500">{locFood.quantity}</span>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(item.name);
                        }}
                        className="p-1 text-gray-400 hover:text-amber-400 transition-colors cursor-pointer"
                        title={t('nutriAddFav', 'افزودن به علاقه‌مندی‌ها')}
                      >
                        <Star className={`w-4 h-4 ${isFav ? 'text-amber-400 fill-amber-400' : ''}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
                      <div className="text-[11px] font-mono text-gray-600 dark:text-gray-300">
                        🔥 <strong>{formatNumberByLang(item.calories, lang)}</strong> kcal | P:{formatNumberByLang(item.protein, lang)}g
                      </div>

                      <button
                        onClick={() => handleOpenFoodSheet(item)}
                        className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow transition-all flex items-center gap-1 cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>{t('nutriLogBtn', 'ثبت')}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 3: ADD CUSTOM MEAL & AI ESTIMATION (REQUIREMENT #4 & #6) */}
      {activeTab === 'custom_add' && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-5">
          <div className="flex items-center gap-2 border-b pb-3 border-gray-100 dark:border-gray-800">
            <Plus className="w-5 h-5 text-emerald-600" />
            <h3 className="text-base font-extrabold font-display text-gray-900 dark:text-white">
              {t('nutriCustomAddTitle', 'ثبت غذا یا تحلیل اتوماتیک هوش مصنوعی')}
            </h3>
          </div>

          <form onSubmit={handleSaveCustomFood} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                {t('nutriFoodNameDesc', 'نام غذا / شرح وعده')} <span className="text-rose-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  required
                  placeholder={t('nutriFoodNamePlaceholder', 'مثلاً: یک بشقاب استانبولی پلو با ماست و سالاد شیرازی')}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />

                <VoiceInputButton
                  currentValue={customName}
                  onUpdate={(val) => setCustomName(val)}
                  disabled={isAiEstimating}
                  title={t('voiceInputNormal', 'ورودی صوتی')}
                />

                <button
                  type="button"
                  onClick={handleAiEstimateCustom}
                  disabled={isAiEstimating || !customName.trim()}
                  className="px-4 py-2.5 rounded-2xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold text-xs flex items-center gap-1.5 shadow transition-all cursor-pointer shrink-0"
                >
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>{isAiEstimating ? t('nutriAiAnalyzing', 'در حال تحلیل AI...') : t('nutriAiEstimate', 'تخمین با AI')}</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('nutriQtyWeight', 'مقدار / وزن / سروینگ')}
                </label>
                <input
                  type="text"
                  placeholder={t('nutriQtyPlaceholder', 'مثلاً: ۱۵۰ گرم / ۱ بشقاب کامل')}
                  value={customQty}
                  onChange={(e) => setCustomQty(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('nutriMealType', 'نوع وعده')}
                </label>
                <select
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value as any)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-2xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="Breakfast">🍳 {t('nutriBreakfast', 'صبحانه')}</option>
                  <option value="Lunch">🍗 {t('nutriLunch', 'ناهار')}</option>
                  <option value="Dinner">🍲 {t('nutriDinner', 'شام')}</option>
                  <option value="Snack">🍎 {t('nutriSnack', 'میان‌وعده')}</option>
                </select>
              </div>
            </div>

            {/* Calories & Macros Input Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-bold text-amber-600 dark:text-amber-400 mb-1">
                  {t('nutriCalLabel', 'کالری (kcal)')}
                </label>
                <input
                  type="number"
                  placeholder="350"
                  value={customCal}
                  onChange={(e) => setCustomCal(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-rose-600 dark:text-rose-400 mb-1">
                  {t('nutriProtLabel', 'پروتئین (گرم)')}
                </label>
                <input
                  type="number"
                  placeholder="25"
                  value={customProt}
                  onChange={(e) => setCustomProt(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {t('nutriCarbLabel', 'کربوهیدرات (گرم)')}
                </label>
                <input
                  type="number"
                  placeholder="40"
                  value={customCarb}
                  onChange={(e) => setCustomCarb(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-emerald-600 dark:text-emerald-400 mb-1">
                  {t('nutriFatLabel', 'چربی (گرم)')}
                </label>
                <input
                  type="number"
                  placeholder="10"
                  value={customFat}
                  onChange={(e) => setCustomFat(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>{t('nutriLogToDiary', 'ثبت در دفترچه روزانه')}</span>
            </button>
          </form>
        </div>
      )}

      {/* SECTION 4: NUTRITION ASSISTANT CHAT (REQUIREMENT #7) */}
      {activeTab === 'assistant' && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
          <div className="flex items-center gap-2 border-b pb-3 border-gray-100 dark:border-gray-800">
            <Sparkles className="w-5 h-5 text-indigo-500" />
            <h3 className="text-base font-extrabold font-display text-gray-900 dark:text-white">
              {t('nutriChatTitle', 'دستیار هوشمند چت تغذیه و رژیم')}
            </h3>
          </div>

          <AiChatBox
            feature="nutrition"
            title={t('nutriChatTitle', 'دستیار چت هوشمند تغذیه')}
            subtitle={t('nutriChatSubtitle', 'سوالات تخصصی رژیم، محاسبه کالری و برنامه غذایی خود را بپرسید')}
            placeholder={t('nutriChatPlaceholder', 'مثلاً: برای کاهش چربی شکم و افزایش عضله چه خوراکی‌هایی توصیه می‌کنی؟')}
            onApplyResult={(parsed) => {
              if (parsed && parsed.calories) {
                const now = new Date();
                const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                const newFoodItem: FoodItem = {
                  id: 'food-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
                  name: parsed.foodName || 'وعده پیشنهادی AI',
                  category: 'Lunch',
                  quantity: parsed.quantity || '۱ سروینگ',
                  calories: parsed.calories,
                  protein: parsed.protein || 0,
                  carbs: parsed.carbs || 0,
                  fat: parsed.fat || 0,
                  time: timeStr
                };
                onUpdateRecord({
                  ...record,
                  foods: [
                    ...(record.foods || []),
                    newFoodItem
                  ]
                });
                if (currentDate) {
                  apiPost('/api/nutrition', {
                    id: newFoodItem.id,
                    date: currentDate,
                    meal_type: newFoodItem.category,
                    food_name: newFoodItem.name,
                    portion: newFoodItem.quantity,
                    calories: newFoodItem.calories,
                    protein_g: newFoodItem.protein || 0,
                    carbs_g: newFoodItem.carbs || 0,
                    fat_g: newFoodItem.fat || 0
                  }).catch((err) => console.warn('Failed saving AI result to nutrition_entries:', err));
                }
                setActiveTab('meals');
              }
            }}
          />
        </div>
      )}

      {/* SECTION 5: WEEKLY REPORT */}
      {activeTab === 'report' && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
          <div className="flex items-center gap-2 border-b pb-3 border-gray-100 dark:border-gray-800">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            <h3 className="text-base font-extrabold font-display text-gray-900 dark:text-white">
              {t('nutriReportTitle', 'گزارش و تحلیل پیشرفت تغذیه‌ای')}
            </h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-3 border border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 block">{t('nutriTotalCalToday', 'مجموع کالری امروز')}</span>
              <span className="text-lg font-black text-amber-600 dark:text-amber-400">{toPersianDigits(totalCalories)}</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-3 border border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 block">{t('nutriTotalProtToday', 'پروتئین امروز')}</span>
              <span className="text-lg font-black text-rose-600 dark:text-rose-400">{toPersianDigits(totalProtein)}g</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-3 border border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 block">{t('nutriTotalCarbToday', 'کربوهیدرات امروز')}</span>
              <span className="text-lg font-black text-blue-600 dark:text-blue-400">{toPersianDigits(totalCarbs)}g</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-3 border border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 block">{t('nutriWaterConsumed', 'آب مصرفی')}</span>
              <span className="text-lg font-black text-cyan-600 dark:text-cyan-400">{toPersianDigits(record.waterIntake || 0)} ml</span>
            </div>
          </div>

          <p className="text-xs text-gray-500 leading-relaxed bg-emerald-50 dark:bg-emerald-950/40 p-3.5 rounded-2xl border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200">
            💡 <strong>{t('nutriDietTip', 'توصیه رژیمی امروز:')}</strong> {t('nutriDietTipText', 'برای دستیابی بهتر به اهداف ورزشی، پیشنهاد می‌شود پروتئین روزانه خود را در تمام ۴ وعده تقسیم کنید و حتماً حداقل ۲.۵ لیتر آب بنوشید.')}
          </p>
        </div>
      )}

      {/* LOG FOOD MODAL SHEET (REQUIREMENT #6 & AI Weight Recalculator) */}
      {selectedTemplate && (() => {
        const computed = getComputedNutrients();
        const locSelected = getLocalizedFoodItem(selectedTemplate.name, selectedTemplate.quantity, lang);
        return (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl border border-gray-100 dark:border-gray-800 space-y-4 animate-in slide-in-from-bottom duration-200">
              <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">{locSelected.name}</h3>
                  <p className="text-xs text-gray-500">{t('nutriRefUnit', 'واحد مرجع:')} {locSelected.quantity} ({formatNumberByLang(selectedTemplate.calories, lang)} kcal)</p>
                </div>
                <button onClick={() => { setSelectedTemplate(null); setAiNutrientResult(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Select Meal Type */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('nutriSelectMeal', 'انتخاب وعده غذایی:')}
                </label>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { id: 'Breakfast', label: `🍳 ${t('nutriBreakfast', 'صبحانه')}` },
                    { id: 'Lunch', label: `🍗 ${t('nutriLunch', 'ناهار')}` },
                    { id: 'Dinner', label: `🍲 ${t('nutriDinner', 'شام')}` },
                    { id: 'Snack', label: `🍎 ${t('nutriSnack', 'میان‌وعده')}` }
                  ].map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSelectedMealType(m.id as any)}
                      className={`py-2 px-1 rounded-xl text-[11px] font-bold text-center transition-all cursor-pointer ${
                        selectedMealType === m.id
                          ? 'bg-emerald-600 text-white shadow'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Serving Multiplier buttons */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('nutriServingMultiplier', 'ضریب واحد مصرفی:')}
                </label>
                <div className="flex items-center gap-2">
                  {[0.5, 1, 1.5, 2, 3].map(mult => (
                    <button
                      key={mult}
                      type="button"
                      onClick={() => {
                        setServingMultiplier(mult);
                        setCustomGramAmount('');
                        setAiNutrientResult(null);
                      }}
                      className={`flex-1 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                        servingMultiplier === mult && !customGramAmount
                          ? 'bg-indigo-600 text-white shadow'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {mult}x
                    </button>
                  ))}
                </div>
              </div>

              {/* Exact Grams Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('nutriOrEnterExactWeight', 'یا وارد کردن وزن دقیق (گرم):')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    placeholder={t('nutriWeightPlaceholder', 'مثلاً: {{base}} (گرم)', { base: toPersianDigits(selectedTemplate.baseGrams || 100) })}
                    value={customGramAmount}
                    onChange={(e) => {
                      setCustomGramAmount(e.target.value);
                      setAiNutrientResult(null);
                    }}
                    className="w-full pl-12 pr-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="absolute left-3 top-2 text-xs text-gray-400">{t('nutriGrams', 'گرم')}</span>
                </div>
              </div>

              {/* AI Recalculate Button */}
              <button
                type="button"
                onClick={handleAiRecalculateSheet}
                disabled={isAiCalculatingSheet}
                className="w-full py-2.5 px-4 rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:to-pink-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all cursor-pointer disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                <span>
                  {isAiCalculatingSheet ? t('nutriAiRecalculating', 'در حال محاسبه دقیق وزن با AI...') : t('nutriAiQueryWeight', 'استعلام کالری و درشت‌مغذی این وزن از هوش مصنوعی')}
                </span>
              </button>

              {/* Calculated Preview & Macro Breakdown */}
              <div className="bg-emerald-50 dark:bg-emerald-950/40 p-3.5 rounded-2xl border border-emerald-200 dark:border-emerald-800 text-xs space-y-2">
                <div className="flex items-center justify-between border-b border-emerald-200/60 dark:border-emerald-800/60 pb-2">
                  <span className="font-bold text-emerald-950 dark:text-emerald-100 flex items-center gap-1.5">
                    <span>{t('nutriCalcResult', 'ارزش غذایی محاسبه‌شده')} ({computed.quantity}):</span>
                  </span>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                    computed.source === 'ai'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100'
                  }`}>
                    {computed.source === 'ai' ? t('nutriSourceAi', '✨ هوش مصنوعی') : t('nutriSourceStd', '⚡ محاسبه استاندار')}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center pt-1 font-mono">
                  <div className="bg-white/80 dark:bg-gray-900/80 p-2 rounded-xl border border-emerald-100 dark:border-emerald-900">
                    <span className="block text-[10px] text-gray-500">🔥 {t('nutriCalories', 'کالری')}</span>
                    <strong className="text-emerald-700 dark:text-emerald-300 text-xs">{toPersianDigits(computed.calories)}</strong>
                    <span className="text-[9px] text-gray-400 block">{t('nutriKcal', 'kcal')}</span>
                  </div>
                  <div className="bg-white/80 dark:bg-gray-900/80 p-2 rounded-xl border border-emerald-100 dark:border-emerald-900">
                    <span className="block text-[10px] text-gray-500">🍗 {t('nutriProtein', 'پروتئین')}</span>
                    <strong className="text-amber-700 dark:text-amber-300 text-xs">{toPersianDigits(computed.protein)}</strong>
                    <span className="text-[9px] text-gray-400 block">{t('nutriGrams', 'گرم')}</span>
                  </div>
                  <div className="bg-white/80 dark:bg-gray-900/80 p-2 rounded-xl border border-emerald-100 dark:border-emerald-900">
                    <span className="block text-[10px] text-gray-500">🌾 {t('nutriCarbs', 'کربوهیدرات')}</span>
                    <strong className="text-blue-700 dark:text-blue-300 text-xs">{toPersianDigits(computed.carbs)}</strong>
                    <span className="text-[9px] text-gray-400 block">{t('nutriGrams', 'گرم')}</span>
                  </div>
                  <div className="bg-white/80 dark:bg-gray-900/80 p-2 rounded-xl border border-emerald-100 dark:border-emerald-900">
                    <span className="block text-[10px] text-gray-500">🥑 {t('nutriFat', 'چربی')}</span>
                    <strong className="text-rose-700 dark:text-rose-300 text-xs">{toPersianDigits(computed.fat)}</strong>
                    <span className="text-[9px] text-gray-400 block">{t('nutriGrams', 'گرم')}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleConfirmAddFood}
                className="w-full py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{t('nutriLogToFoodDiary', 'ثبت در دفترچه غذایی')}</span>
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Subcomponent for Macro Progress Card
interface MacroProgressCardProps {
  title: string;
  icon: string;
  current: number;
  target: number;
  unit: string;
  color: string;
  bgColor: string;
}

function MacroProgressCard({ title, icon, current, target, unit, color, bgColor }: MacroProgressCardProps) {
  const { t } = useLanguage();
  const percent = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;

  return (
    <div className={`p-3.5 rounded-2xl border border-white/10 ${bgColor} backdrop-blur-md space-y-2`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-200 flex items-center gap-1">
          <span>{icon}</span>
          <span>{title}</span>
        </span>
        <span className="text-[11px] font-mono text-emerald-300 font-bold">{toPersianDigits(percent)}%</span>
      </div>

      <div className="flex items-baseline justify-between text-xs">
        <span className="text-lg font-black text-white font-mono">{toPersianDigits(current)}</span>
        <span className="text-[11px] text-gray-300 font-mono">{t('nutriFrom', 'از')} {toPersianDigits(target)} {unit}</span>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-2 bg-black/20 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full bg-gradient-to-r ${color} transition-all duration-500`} 
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
